import { useRef } from 'react'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Color, TextStyle } from '@tiptap/extension-text-style'
import { Highlight } from '@tiptap/extension-highlight'
import { TextAlign } from '@tiptap/extension-text-align'
import { TableKit } from '@tiptap/extension-table'
import { Image as ImageNode } from '@tiptap/extension-image'
import { formatAmount } from '@dorixona/utils'
import { cleanHtml, dropPastedColors } from './editor'
import { MAX_BODY } from './pages'
import { RichTextToolbar } from './RichTextToolbar'

// Один набор на все редакторы: новый массив на каждый рендер заставлял бы
// редактор каждый раз сверять настройки заново.
const EXTENSIONS = [
  StarterKit.configure({
    // Заголовок страницы — отдельное поле; в тексте хватает трёх уровней,
    // h4–h6 из вставки становятся абзацами.
    heading: { levels: [1, 2, 3] },
    // Ссылка в редакторе не открывается по нажатию: нажатие ставит курсор.
    link: { openOnClick: false, autolink: true },
  }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  // Ширину столбцов не тянем мышью: на телефоне покупателя её всё равно нет.
  TableKit.configure({ table: { resizable: false } }),
  // Картинки — только по адресу: base64 раздул бы текст за предел сервера.
  ImageNode.configure({ allowBase64: false }),
]

export interface RichTextProps {
  /** Текст в HTML, с которым редактор открывается. */
  html: string
  /** id подписи поля: редактор называется так же, как поле. */
  labelledBy: string
  lang: string
  readOnly: boolean
  invalid: boolean
  describedBy?: string
  /**
   * Текст в HTML после правки; пустой редактор — пустая строка. Вернули
   * текст к тому, с каким редактор открылся, — приходит исходная строка
   * `html`, а не её пересказ редактором: так отменённая правка не выглядит
   * изменением.
   */
  onChange: (html: string) => void
}

/**
 * Редактор форматированного текста страницы. Внутри — Tiptap, наружу — HTML:
 * сервер хранит его как есть, приложение покупателя показывает очищенным.
 * Что редактор не знает (шрифты, размеры, классы из Word), схема отбрасывает
 * при разборе, поэтому вставка из документов сохраняет структуру, а не вид.
 *
 * Редактор не управляемый: он открывается с текстом и сообщает правки.
 * Другой язык или сброс правок — это новый редактор (`key` у вызывающего),
 * поэтому «Отменить правку» не уводит в текст соседнего языка.
 */
export function RichText({
  html,
  labelledBy,
  lang,
  readOnly,
  invalid,
  describedBy,
  onChange,
}: RichTextProps) {
  // Редактор приводит HTML к своему виду (атрибуты, порядок меток). Правки
  // сравниваем с этим видом, а не с исходной строкой.
  const opened = useRef('')
  const initial = useRef(html)
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: html,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'st-rte-doc',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelledBy,
        'aria-readonly': String(readOnly),
        'aria-invalid': String(invalid),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        lang,
      },
      transformPastedHTML: dropPastedColors,
    },
    onCreate: ({ editor: created }) => {
      opened.current = cleanHtml(created.getHTML())
    },
    onUpdate: ({ editor: changed }) => {
      const next = cleanHtml(changed.getHTML())
      onChange(next === opened.current ? initial.current : next)
    },
  })

  return (
    <div className="st-rte" data-invalid={invalid || undefined}>
      {readOnly ? null : <RichTextToolbar editor={editor} />}
      <EditorContent editor={editor} />
      {readOnly ? null : <Counter editor={editor} />}
    </div>
  )
}

/**
 * Сколько знаков HTML занимает текст. Предел сервера — на хранимую строку,
 * поэтому считаем её длину, а не видимые буквы: таблица или цвет тоже
 * расходуют место.
 */
function Counter({ editor }: { editor: Editor }) {
  const length = useEditorState({
    editor,
    selector: ({ editor: e }) => cleanHtml(e.getHTML()).length,
  })
  return (
    <div className="st-rte-foot" data-over={length > MAX_BODY || undefined}>
      {formatAmount(length)} / {formatAmount(MAX_BODY)}
    </div>
  )
}
